/**
 * Admin Clips API Service
 * 
 * Handles all API interactions for admin clip management,
 * including CRUD operations, state transitions, and job triggers.
 */

import logger from '../core/logger.js';
import { authService } from './auth-service.js';
import { ClipState } from '../types/rbac.js';
import {
  AdminClip,
  ClipsFilters,
  ClipsListResponse,
  CreateClipRequest,
  UpdateClipRequest,
  StateTransitionRequest,
  RegenerationRequest,
  AudioGenerationRequest,
  JobCreationResponse,
  ValidationResponse,
  Job,
  CreateChangeRequestRequest,
  VersionsListResponse,
  RollbackRequest,
  VersionComparison
} from '../types/admin-clip.js';

/**
 * AdminClipsService class
 */
class AdminClipsService {
  // Deprecated: Use consolidated endpoints /api/clips and /api/jobs instead
  // Only kept for legacy transitionState method (currently unused)
  private baseUrl = '/api/admin';

  /**
   * Get auth headers for API requests
   */
  private getAuthHeaders(): HeadersInit {
    const token = authService.getAuthToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Fetch clips list with filters
   * 
   * @param filters - Filter parameters
   * @param page - Page number (default 1)
   * @param perPage - Results per page (default 20)
   * @returns Clips list response
   */
  async listClips(
    filters: ClipsFilters = {},
    page: number = 1,
    perPage: number = 20
  ): Promise<ClipsListResponse> {
    logger.info('admin-clips: Fetching clips list', { filters, page, perPage });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'list',
          page,
          perPage,
          ...filters
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch clips: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Clips list fetched', { count: data.clips.length });
      
      return data;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch clips', { error });
      
      // Return mock data for development
      return this.mockClipsList(filters, page, perPage);
    }
  }

  /**
   * Get a single clip by ID
   * 
   * @param id - Clip ID
   * @returns Admin clip
   */
  async getClip(id: string): Promise<AdminClip> {
    logger.info('admin-clips: Fetching clip', { id });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'get', id })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch clip: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Clip fetched', { id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch clip', { id, error });
      
      // Return mock data for development
      return this.mockClip(id);
    }
  }

  /**
   * Create a new clip
   * 
   * @param request - Create clip request
   * @returns Created clip
   */
  async createClip(request: CreateClipRequest): Promise<AdminClip> {
    logger.info('admin-clips: Creating clip', { request });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'create', ...request })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        const errorMessage = errorData.error || errorData.details || response.statusText;
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Clip created', { id: data.clip.id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to create clip', { error });
      throw error;
    }
  }

  /**
   * Update an existing clip
   * 
   * @param id - Clip ID
   * @param request - Update clip request
   * @returns Updated clip
   */
  async updateClip(id: string, request: UpdateClipRequest): Promise<AdminClip> {
    logger.info('admin-clips: Updating clip', { id, request });
    
    try {
      // Convert camelCase to snake_case for API compatibility
      const apiRequest: any = {};
      
      if (request.title !== undefined) apiRequest.title = request.title;
      if (request.language !== undefined) apiRequest.language = request.language;
      if (request.level !== undefined) apiRequest.level = request.level;
      if (request.format !== undefined) apiRequest.format = request.format;
      if (request.script !== undefined) apiRequest.script = request.script;
      if (request.questions !== undefined) apiRequest.questions = request.questions;
      if (request.speakers !== undefined) apiRequest.speakers = request.speakers;
      if (request.voiceId !== undefined) apiRequest.voice_id = request.voiceId;
      if (request.wordCount !== undefined) apiRequest.word_count = request.wordCount;
      if (request.metadata !== undefined) apiRequest.metadata = request.metadata;
      
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'update', id, updates: apiRequest })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update clip: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Clip updated', { id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to update clip', { id, error });
      throw error;
    }
  }

  /**
   * Submit clip for text review (Draft → TextReview)
   * 
   * @param id - Clip ID
   * @returns Updated clip
   */
  async submitForReview(id: string): Promise<AdminClip> {
    logger.info('admin-clips: Submitting clip for review', { id });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'submit', id })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to submit for review: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Clip submitted for review', { id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to submit for review', { id, error });
      throw error;
    }
  }

  /**
   * Approve text (TextReview → TextApproved)
   * 
   * @param id - Clip ID
   * @returns Updated clip
   */
  async approveText(id: string): Promise<AdminClip> {
    logger.info('admin-clips: Approving text', { id });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'approve-text', id })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to approve text: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Text approved', { id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to approve text', { id, error });
      throw error;
    }
  }

  /**
   * Approve audio (AudioReview → AudioApproved)
   * 
   * @param id - Clip ID
   * @returns Updated clip
   */
  async approveAudio(id: string): Promise<AdminClip> {
    logger.info('admin-clips: Approving audio', { id });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'approve-audio', id })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to approve audio: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Audio approved', { id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to approve audio', { id, error });
      throw error;
    }
  }

  /**
   * Publish clip (AudioApproved → Published)
   * 
   * @param id - Clip ID
   * @returns Updated clip
   */
  async publishClip(id: string): Promise<AdminClip> {
    logger.info('admin-clips: Publishing clip', { id });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'publish', id })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to publish clip: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Clip published', { id });
      
      return data.clip;
    } catch (error) {
      logger.error('admin-clips: Failed to publish clip', { id, error });
      throw error;
    }
  }

  /**
   * Transition clip state
   * 
   * @param id - Clip ID
   * @param toState - Target state
   * @param context - Optional context
   * @returns Updated clip
   */
  async transitionState(
    id: string,
    toState: ClipState,
    context?: Record<string, unknown>
  ): Promise<AdminClip> {
    logger.info('admin-clips: Transitioning state', { id, toState, context });
    
    try {
      const request: StateTransitionRequest = { toState, context };
      
      const response = await fetch(`${this.baseUrl}/clips/${id}/transition`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to transition state: ${response.statusText}`);
      }
      
      const clip = await response.json();
      logger.info('admin-clips: State transitioned', { id, toState });
      
      return clip;
    } catch (error) {
      logger.error('admin-clips: Failed to transition state', { id, toState, error });
      throw error;
    }
  }

  /**
   * Trigger regeneration job
   * 
   * @param id - Clip ID
   * @param request - Regeneration request
   * @returns Job creation response
   */
  async triggerRegeneration(
    id: string,
    request: RegenerationRequest
  ): Promise<JobCreationResponse> {
    logger.info('admin-clips: Triggering regeneration', { id, request });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'regenerate',
          id,
          promptId: request.promptVersionId,
          modelId: request.modelId,
          parameters: request.parameters
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to trigger regeneration: ${response.statusText}`);
      }
      
      const result = await response.json();
      logger.info('admin-clips: Regeneration triggered', { id, jobId: result.job.id });
      
      return result;
    } catch (error) {
      logger.error('admin-clips: Failed to trigger regeneration', { id, error });
      throw error;
    }
  }

  /**
   * Trigger audio generation job
   * 
   * @param id - Clip ID
   * @param request - Audio generation request
   * @returns Job creation response
   */
  async triggerAudioGeneration(
    id: string,
    request: AudioGenerationRequest
  ): Promise<JobCreationResponse> {
    logger.info('admin-clips: Triggering audio generation', { id, request });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'queue-audio',
          id,
          voiceId: request.voiceId,
          voiceMapping: request.voiceMapping
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to trigger audio generation: ${response.statusText}`);
      }
      
      const result = await response.json();
      logger.info('admin-clips: Audio generation triggered', { id, jobId: result.job.id });
      
      return result;
    } catch (error) {
      logger.error('admin-clips: Failed to trigger audio generation', { id, error });
      throw error;
    }
  }

  /**
   * Get job status
   * 
   * @param jobId - Job ID
   * @returns Job
   */
  async getJob(jobId: string): Promise<Job> {
    logger.info('admin-clips: Fetching job', { jobId });
    
    try {
      const response = await fetch('/api/jobs', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'get',
          id: jobId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch job: ${response.statusText}`);
      }
      
      const result = await response.json();
      logger.info('admin-clips: Job fetched', { jobId, status: result.job.status });
      
      return result.job;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch job', { jobId, error });
      throw error;
    }
  }

  /**
   * List jobs for a clip
   * 
   * @param clipId - Clip ID
   * @param type - Optional job type filter
   * @param page - Page number (default 1)
   * @param perPage - Results per page (default 20)
   * @returns Jobs list
   */
  async listJobs(
    clipId: string, 
    type?: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<{ jobs: Job[]; total: number; page: number; perPage: number }> {
    logger.info('admin-clips: Fetching jobs for clip', { clipId, type });
    
    try {
      const response = await fetch('/api/jobs', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'list',
          clipId,
          type,
          page,
          perPage
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.statusText}`);
      }
      
      const result = await response.json();
      logger.info('admin-clips: Jobs fetched', { clipId, count: result.jobs.length });
      
      return result;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch jobs', { clipId, error });
      throw error;
    }
  }

  /**
   * Get the latest regeneration job for a clip
   * 
   * @param clipId - Clip ID
   * @returns Latest regeneration job or null if none exists
   */
  async getLatestRegenerationJob(clipId: string): Promise<Job | null> {
    logger.info('admin-clips: Fetching latest regeneration job', { clipId });
    
    try {
      const result = await this.listJobs(clipId, 'regeneration', 1, 1);
      
      if (result.jobs && result.jobs.length > 0) {
        return result.jobs[0] || null;
      }
      
      return null;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch latest regeneration job', { clipId, error });
      return null;
    }
  }

  /**
   * List all versions of a clip
   * 
   * @param clipId - Clip ID (constant across versions)
   * @returns Versions list response
   */
  async listVersions(clipId: string): Promise<VersionsListResponse> {
    logger.info('admin-clips: Fetching versions', { clipId });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'list-versions', clipId })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch versions: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Versions fetched', { clipId, count: data.versions.length });
      
      return data;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch versions', { clipId, error });
      throw error;
    }
  }

  /**
   * Get a specific version of a clip
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @returns Admin clip (specific version)
   */
  async getVersion(clipId: string, version: number): Promise<AdminClip> {
    logger.info('admin-clips: Fetching version', { clipId, version });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'get-version', clipId, version })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version fetched', { clipId, version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch version', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Create a change request (new version) for a published clip
   * 
   * @param clipId - Clip ID
   * @param request - Change request parameters
   * @returns Created version
   */
  async createChangeRequest(
    clipId: string, 
    request: CreateChangeRequestRequest
  ): Promise<AdminClip> {
    logger.info('admin-clips: Creating change request', { clipId, request });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'create-version', clipId, ...request })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to create change request: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Change request created', { clipId, version: data.version.version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to create change request', { clipId, error });
      throw error;
    }
  }

  /**
   * Update a specific version
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @param request - Update request
   * @returns Updated version
   */
  async updateVersion(
    clipId: string, 
    version: number, 
    request: UpdateClipRequest
  ): Promise<AdminClip> {
    logger.info('admin-clips: Updating version', { clipId, version, request });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'update-version', clipId, version, updates: request })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update version: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version updated', { clipId, version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to update version', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Submit version for text review
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @returns Updated version
   */
  async submitVersionForReview(clipId: string, version: number): Promise<AdminClip> {
    logger.info('admin-clips: Submitting version for review', { clipId, version });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'version-submit', clipId, version })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to submit version: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version submitted', { clipId, version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to submit version', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Approve text for a version
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @returns Updated version
   */
  async approveVersionText(clipId: string, version: number): Promise<AdminClip> {
    logger.info('admin-clips: Approving version text', { clipId, version });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'version-approve-text', clipId, version })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to approve text: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version text approved', { clipId, version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to approve version text', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Queue audio generation for a version
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @param request - Audio generation request (or voiceId/voiceMapping directly)
   * @returns Job creation response
   */
  async queueVersionAudio(
    clipId: string, 
    version: number,
    requestOrVoiceId?: AudioGenerationRequest | string,
    voiceMapping?: Record<string, string>
  ): Promise<JobCreationResponse> {
    // Support both old and new API
    let voiceId: string | undefined;
    let mapping: Record<string, string> | undefined;
    
    if (typeof requestOrVoiceId === 'string') {
      // New API: queueVersionAudio(clipId, version, voiceId, voiceMapping)
      voiceId = requestOrVoiceId;
      mapping = voiceMapping;
    } else if (requestOrVoiceId) {
      // Old API: queueVersionAudio(clipId, version, request)
      voiceId = requestOrVoiceId.voiceId;
      mapping = requestOrVoiceId.voiceMapping;
    } else if (voiceMapping) {
      // New API with only voiceMapping: queueVersionAudio(clipId, version, undefined, voiceMapping)
      mapping = voiceMapping;
    }
    
    logger.info('admin-clips: Queuing version audio', { clipId, version, voiceId, voiceMapping: mapping });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ 
          action: 'version-queue-audio', 
          clipId, 
          version,
          voiceId,
          voiceMapping: mapping
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to queue audio: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version audio queued', { clipId, version, jobId: data.job?.id });
      
      return {
        job: data.job,
        version: data.version
      };
    } catch (error) {
      logger.error('admin-clips: Failed to queue version audio', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Approve audio for a version
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @returns Updated version
   */
  async approveVersionAudio(clipId: string, version: number): Promise<AdminClip> {
    logger.info('admin-clips: Approving version audio', { clipId, version });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'version-approve-audio', clipId, version })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to approve audio: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version audio approved', { clipId, version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to approve version audio', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Publish a version
   * 
   * @param clipId - Clip ID
   * @param version - Version number
   * @returns Published version
   */
  async publishVersion(clipId: string, version: number): Promise<AdminClip> {
    logger.info('admin-clips: Publishing version', { clipId, version });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'version-publish', clipId, version })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to publish version: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version published', { clipId, version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to publish version', { clipId, version, error });
      throw error;
    }
  }

  /**
   * Rollback to a previous version (Admin only)
   * 
   * @param clipId - Clip ID
   * @param request - Rollback request
   * @returns New rollback version
   */
  async rollbackVersion(clipId: string, request: RollbackRequest): Promise<AdminClip> {
    logger.info('admin-clips: Rolling back version', { clipId, request });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'rollback', clipId, ...request })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to rollback: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version rolled back', { clipId, newVersion: data.version.version });
      
      return data.version;
    } catch (error) {
      logger.error('admin-clips: Failed to rollback version', { clipId, error });
      throw error;
    }
  }

  /**
   * Get diff between two versions
   * 
   * @param clipId - Clip ID
   * @param fromVersion - Starting version (or 'latest')
   * @param toVersion - Target version (or 'latest')
   * @returns Version comparison
   */
  async getVersionDiff(
    clipId: string, 
    fromVersion: number | 'latest', 
    toVersion: number | 'latest'
  ): Promise<VersionComparison> {
    logger.info('admin-clips: Getting version diff', { clipId, fromVersion, toVersion });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ 
          action: 'version-diff', 
          clipId, 
          from: fromVersion.toString(),
          to: toVersion.toString()
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get diff: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Version diff fetched', { 
        clipId, 
        fromVersion: data.fromVersion, 
        toVersion: data.toVersion,
        diffCount: data.diffs.length 
      });
      
      return data;
    } catch (error) {
      logger.error('admin-clips: Failed to get version diff', { clipId, error });
      throw error;
    }
  }

  /**
   * Mock clips list for development
   */
  private mockClipsList(
    filters: ClipsFilters,
    page: number,
    perPage: number
  ): ClipsListResponse {
    logger.warn('admin-clips: Using mock clips list (API not implemented)');
    
    const mockClips: AdminClip[] = [
      this.mockClip('clip-001', 'Draft'),
      this.mockClip('clip-002', 'TextReview'),
      this.mockClip('clip-003', 'TextApproved'),
      this.mockClip('clip-004', 'AudioQueued'),
      this.mockClip('clip-005', 'AudioReady'),
      this.mockClip('clip-006', 'AudioReview'),
      this.mockClip('clip-007', 'AudioApproved'),
      this.mockClip('clip-008', 'Published')
    ];
    
    // Apply filters
    let filtered = mockClips;
    if (filters.state) {
      filtered = filtered.filter(c => c.state === filters.state);
    }
    if (filters.language) {
      filtered = filtered.filter(c => c.language === filters.language);
    }
    if (filters.level) {
      filtered = filtered.filter(c => c.level === filters.level);
    }
    
    // Pagination
    const start = (page - 1) * perPage;
    const end = start + perPage;
    const paginatedClips = filtered.slice(start, end);
    
    return {
      clips: paginatedClips,
      total: filtered.length,
      page,
      perPage,
      hasMore: end < filtered.length
    };
  }

  /**
   * Run validation on a clip
   * 
   * @param clipId - Clip ID to validate
   * @param options - Validation options
   * @returns Validation response with report
   */
  async validateClip(clipId: string, options?: { skipTools?: string[] }): Promise<ValidationResponse> {
    logger.info('admin-clips: Validating clip', { clipId, options });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'validate',
          id: clipId,
          options
        })
      });
      
      if (!response.ok) {
        throw new Error(`Validation failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Validation complete', { 
        clipId, 
        status: data.report?.overallStatus 
      });
      
      return data;
    } catch (error) {
      logger.error('admin-clips: Validation failed', { clipId, error });
      throw error;
    }
  }

  /**
   * Queue regeneration job for a clip
   * 
   * @param clipId - Clip ID to regenerate
   * @param promptId - Prompt version to use
   * @param modelId - Model to use
   * @param parameters - Optional model parameters
   * @returns Job creation response
   */
  async regenerateClip(
    clipId: string,
    promptId: string,
    modelId: string,
    parameters?: Record<string, unknown>
  ): Promise<JobCreationResponse> {
    logger.info('admin-clips: Queueing regeneration', { 
      clipId, promptId, modelId, parameters 
    });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'regenerate',
          id: clipId,
          promptId,
          modelId,
          parameters
        })
      });
      
      if (!response.ok) {
        throw new Error(`Regeneration failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Regeneration job queued', { 
        clipId, 
        jobId: data.job?.id 
      });
      
      return data;
    } catch (error) {
      logger.error('admin-clips: Failed to queue regeneration', { clipId, error });
      throw error;
    }
  }

  /**
   * Get validation history for a clip
   * 
   * @param clipId - Clip ID
   * @param limit - Maximum number of results
   * @returns Validation history
   */
  async getValidationHistory(clipId: string, limit: number = 10): Promise<any> {
    logger.info('admin-clips: Fetching validation history', { clipId, limit });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'get-validation-history',
          clipId,
          limit
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch validation history: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Validation history fetched', { 
        clipId, 
        count: data.history?.length 
      });
      
      return data.history || [];
    } catch (error) {
      logger.error('admin-clips: Failed to fetch validation history', { clipId, error });
      return [];
    }
  }

  /**
   * Fetch available voices, optionally filtered by language and gender
   * 
   * @param language - Language code filter (optional)
   * @param gender - Gender filter (optional)
   * @returns List of available voices with defaults and dialogue config
   */
  async fetchVoices(language?: string, gender?: string): Promise<import('../types/voice.js').VoicesListResponse> {
    logger.info('admin-clips: Fetching voices', { language, gender });
    
    try {
      const response = await fetch('/api/voices', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'list',
          language,
          gender
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Voices fetched', { count: data.voices?.length });
      
      return data;
    } catch (error) {
      logger.error('admin-clips: Failed to fetch voices', { error });
      throw error;
    }
  }

  /**
   * Queue audio generation for a clip
   * 
   * @param clipId - Clip ID
   * @param voiceId - Voice ID for monologue clips
   * @param voiceMapping - Voice mapping for dialogue clips
   * @returns Job creation response
   */
  async queueAudio(
    clipId: string, 
    voiceId?: string, 
    voiceMapping?: Record<string, string>
  ): Promise<JobCreationResponse> {
    logger.info('admin-clips: Queuing audio generation', { clipId, voiceId, voiceMapping });
    
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'queue-audio',
          id: clipId,
          voiceId,
          voiceMapping
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to queue audio: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('admin-clips: Audio queued successfully', { clipId, jobId: data.job?.id });
      
      return {
        job: data.job,
        clip: data.clip
      };
    } catch (error) {
      logger.error('admin-clips: Failed to queue audio', { clipId, error });
      throw error;
    }
  }

  /**
   * Mock single clip for development
   */
  private mockClip(id: string, state: string = 'Draft'): AdminClip {
    return {
      id,
      title: `Sample Clip ${id}`,
      language: 'tr',
      level: 'B2',
      format: 'dialogue',
      state: state as ClipState,
      version: 1,
      script: 'Sample script content...',
      questions: [],
      createdBy: 'user-001',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
}

// Create and export singleton instance
export const adminClipsService = new AdminClipsService();

// Export class for testing
export { AdminClipsService };
